import { queryOptions } from "@tanstack/react-query"
import { AuthError, fetchAuthSession } from "aws-amplify/auth"
import {
  GatedSavedPaymentMethodsFailReason,
  GatedSetupIntentWalletFailReason,
} from "ipctg-inflow-types/InFlowEnums"

import api from "@/utilities/api"

import {
  GatedAccountError,
  GatedSavedPaymentMethodsError,
  GatedSetupIntentWalletError,
} from "@/utilities/expectedErrors"

import {
  ME_BASE_KEY,
  SAVED_PAYMENT_METHODS_BASE_KEY,
  SAVED_PAYMENT_METHODS_WALLET_INTENT,
} from "./keys"

export const meQueryOptions = () => {
  return queryOptions({
    queryKey: [ME_BASE_KEY],
    queryFn: async () => {
      try {
        const session = await fetchAuthSession()
        const cognitoIDToken = session.tokens?.idToken
        if (cognitoIDToken) {
          const [cognitoUser, inflowUser] = await Promise.all([
            api.auth.fetchUserAttributes(),
            api.account.POST({ cognitoIDToken: cognitoIDToken.toString() }),
          ])

          if ("failReason" in inflowUser) {
            throw new GatedAccountError(inflowUser.failReason)
          }

          return { cognitoUser, inflowUser }
        }
        return null
      } catch (e: unknown) {
        if (
          e instanceof AuthError &&
          e.name === "UserUnAuthenticatedException"
        ) {
          return null
        }
        console.error(e)
        throw e
      }
    },
    retry: false,
  })
}

export const savedPaymentMethodsOptions = () => {
  return queryOptions({
    queryKey: [ME_BASE_KEY, SAVED_PAYMENT_METHODS_BASE_KEY],
    queryFn: async () => {
      const session = await fetchAuthSession()
      const cognitoIDToken = session.tokens?.idToken?.toString()

      if (!cognitoIDToken)
        throw new GatedSavedPaymentMethodsError(
          GatedSavedPaymentMethodsFailReason.Unauthorized
        )

      const rsp = await api.savedPaymentMethods.POST({ cognitoIDToken })
      if ("failReason" in rsp) {
        if (
          rsp.failReason ===
          GatedSavedPaymentMethodsFailReason.NoSavedPaymentMethods
        )
          return { paymentMethods: [] }
        throw new GatedSavedPaymentMethodsError(rsp.failReason)
      }
      return rsp
    },
  })
}

export const walletSetupIntentOptions = () => {
  return queryOptions({
    queryKey: [
      ME_BASE_KEY,
      SAVED_PAYMENT_METHODS_BASE_KEY,
      SAVED_PAYMENT_METHODS_WALLET_INTENT,
    ],
    queryFn: async () => {
      const { cognitoIDToken } = await api.auth.fetchAuthSession()
      if (!cognitoIDToken) {
        throw new GatedSetupIntentWalletError(
          GatedSetupIntentWalletFailReason.Unauthorized
        )
      }
      const rsp = await api.setupIntentWallet.POST({
        cognitoIDToken,
      })
      if ("failReason" in rsp) {
        throw new GatedSetupIntentWalletError(rsp.failReason)
      }
      return rsp
    },
  })
}
